import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ExternalLink, Newspaper } from "lucide-react";
import api from "@/lib/api";

function timeAgo(iso) {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const CATEGORIES = ["All", "Markets", "IPO", "Real Estate", "Commodities", "Crypto", "Economy"];

export default function LiveNewsFeed({ limit = 12, compact = false, defaultCategory = "All", showCategoryFilter = true }) {
  const [category, setCategory] = useState(defaultCategory);
  const { data, isLoading } = useQuery({
    queryKey: ["news", category, limit],
    queryFn: async () => (await api.get(`/news?limit=${limit}&category=${encodeURIComponent(category)}`)).data,
    refetchInterval: 90_000,
  });

  const items = data?.data || [];

  return (
    <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden card-glow" data-testid="live-news-feed">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <span className="pulse-dot inline-block w-2 h-2 rounded-full bg-[#D91E2A]" />
          <Newspaper className="w-4 h-4 text-slate-500" />
          <span className="font-heading font-semibold text-slate-900">Live Newsroom</span>
        </div>
        <span className="text-[10px] font-mono-fin text-slate-500 uppercase tracking-widest">Live</span>
      </div>
      {showCategoryFilter && (
        <div className="flex gap-1 px-4 pt-3 pb-2 border-b border-slate-200 overflow-x-auto no-scrollbar">
          {CATEGORIES.map(c => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`text-[11px] px-2.5 py-1 rounded-md whitespace-nowrap transition-all font-semibold ${category === c ? "bg-[#D91E2A]/10 text-[#D91E2A] border border-[#D91E2A]/30" : "text-slate-600 hover:text-slate-900 border border-transparent"}`}
              data-testid={`news-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >{c}</button>
          ))}
        </div>
      )}
      <div className={`${compact ? "max-h-[520px]" : "max-h-[720px]"} overflow-y-auto divide-y divide-slate-200`}>
        {isLoading && <div className="p-6 text-sm text-slate-500 font-mono-fin">Loading feed…</div>}
        {items.map(n => (
          <Link
            key={n.id}
            to={`/news/${n.slug || n.id}`}
            className="block px-5 py-3.5 hover:bg-slate-50 transition-colors group"
            data-testid={`news-item-${n.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[10px] font-mono-fin uppercase tracking-widest text-[#D91E2A] font-semibold">{n.source}</span>
                  {n.category && <span className="text-[9px] font-mono-fin uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{n.category}</span>}
                  <span className="text-[10px] font-mono-fin text-slate-500">· {timeAgo(n.published)} ago</span>
                </div>
                <h4 className="text-sm font-semibold text-slate-900 group-hover:text-[#D91E2A] leading-snug">{n.title}</h4>
                {!compact && n.summary && (
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{n.summary.replace(/<[^>]*>/g, "")}</p>
                )}
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#D91E2A] mt-1 flex-shrink-0" />
            </div>
          </Link>
        ))}
        {items.length === 0 && !isLoading && <div className="p-6 text-sm text-slate-500 font-mono-fin">No news right now.</div>}
      </div>
    </div>
  );
}
